import React, { useState, useEffect } from 'react';
import { saveToStorage, StorageKeys } from '../utils/storage';
import { getStableReportCount } from '../utils/reportCounter';
import Navigation from './Navigation';

const LandingPage = ({ onStartDiscovery }) => {
  const [idea, setIdea] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [showInspiration, setShowInspiration] = useState(false);
  const [selectedExample, setSelectedExample] = useState(null);
  const [reportsGenerated, setReportsGenerated] = useState(27);
  const [isScrolled, setIsScrolled] = useState(false);

  // Update report counter on component mount and every 5 minutes
  useEffect(() => {
    const updateCounter = () => {
      setReportsGenerated(getStableReportCount());
    };
    
    // Update immediately
    updateCounter();
    
    // Update every 5 minutes (300,000 milliseconds)
    const interval = setInterval(updateCounter, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle scroll detection for logo size
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const centerLogoVisible = scrollPosition < 100; // Trigger at 100px
      setIsScrolled(!centerLogoVisible);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStartDiscovery = () => {
    if (!idea.trim()) return;
    
    setIsLoading(true);
    saveToStorage(StorageKeys.USER_IDEA, idea);
    saveToStorage(StorageKeys.CURRENT_STEP, 2);
    
    setTimeout(() => {
      onStartDiscovery(idea);
      setIsLoading(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleStartDiscovery();
    }
  };

  const industries = ['SaaS', 'Mobile App', 'E-commerce', 'Marketplace', 'Other'];
  
  const examples = [
    {
      text: "A mobile app that helps people find parking spaces",
      metric: "Market Size: 2.3M users",
      industry: "Mobile App"
    },
    {
      text: "An online platform connecting freelance tutors with students",
      metric: "Growth Rate: +12% annually",
      industry: "Marketplace"
    },
    {
      text: "A subscription service for eco-friendly household products",
      metric: "Competition Level: Low",
      industry: "E-commerce"
    }
  ];

  const inspirationExamples = [
    "AI-powered fitness coaching app for remote workers",
    "B2B platform for small business invoice management",
    "Social network for neighborhood community building",
    "Virtual reality therapy sessions for anxiety treatment",
    "Blockchain-based supply chain tracking for sustainable fashion",
    "Voice-activated smart home security system",
    "Peer-to-peer car sharing in rural areas",
    "AR try-on solution for online furniture shopping",
    "Automated meal planning based on dietary restrictions",
    "Real-time language translation for business meetings",
    "Gamified recycling rewards platform",
    "On-demand elderly care companion services",
    "Cryptocurrency payment gateway for small retailers",
    "AI-driven personalized skincare recommendations",
    "Virtual coworking spaces for digital nomads",
    "Smart irrigation system for urban gardening",
    "Subscription box for rare book collectors",
    "Drone delivery service for medical supplies",
    "Social media scheduler with AI content suggestions",
    "Peer-to-peer skill sharing marketplace"
  ];

  const getCharacterCount = () => {
    const count = idea.length;
    if (count < 20) return { color: 'text-gray-400', status: 'Too short' };
    if (count < 150) return { color: 'text-amber-500', status: 'Getting better' };
    if (count < 300) return { color: 'text-green-500', status: 'Perfect length!' };
    return { color: 'text-amber-500', status: 'Consider shortening' };
  };

  const handleExampleClick = (example) => {
    setIdea(example.text);
    setSelectedExample(example);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navigation reportsGenerated={reportsGenerated} />

      <div className="max-w-5xl mx-auto px-4 py-4">
        {/* Header */}
        <div id="home" className="text-center mb-8">
          {/* Main Vibality Logo */}
          <div className="mb-1">
            <img 
              src="/vibality-logo.svg" 
              alt="Vibality" 
              className="h-64 w-auto mx-auto mb-0"
            />
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Find your <span className="text-primary-600">$10M market</span><br />
            opportunity in 3 minutes
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
            See what 52M+ Reddit users are struggling with, then validate your solution with proven pain point discovery.
          </p>
          
          {/* Methodology Section */}
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-gradient-to-r from-sage-50 to-cream-50 rounded-2xl p-8 border-2 border-sage-200 shadow-lg">
              <p className="text-xl font-bold text-gray-900 mb-6 text-center">Powered by proven methodologies:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                <div className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-sm border border-sage-100">
                  <span className="text-sage-600 font-bold text-xl">✓</span>
                  <span className="text-gray-800">
                    <strong className="text-sage-700 text-lg">Jobs-to-be-Done Framework</strong><br/>
                    <span className="text-sm text-gray-600">(Used by Intercom, Stripe, HubSpot)</span>
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-sm border border-sage-100">
                  <span className="text-sage-600 font-bold text-xl">✓</span>
                  <span className="text-gray-800">
                    <strong className="text-sage-700 text-lg">Customer Development Process</strong><br/>
                    <span className="text-sm text-gray-600">(Used by 1,000+ YC companies)</span>
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-sm border border-sage-100">
                  <span className="text-sage-600 font-bold text-xl">✓</span>
                  <span className="text-gray-800">
                    <strong className="text-sage-700 text-lg">Problem-Solution Fit Analysis</strong><br/>
                    <span className="text-sm text-gray-600">(Lean Startup method - Airbnb, Dropbox, Slack)</span>
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-white rounded-xl shadow-sm border border-sage-100">
                  <span className="text-sage-600 font-bold text-xl">✓</span>
                  <span className="text-gray-800">
                    <strong className="text-sage-700 text-lg">Value Proposition Canvas</strong><br/>
                    <span className="text-sm text-gray-600">(Used by Microsoft, 3M, Mastercard)</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto border border-gray-100">

          {/* Main Input */}
          <div className="mb-6">
            <label 
              htmlFor="business-idea" 
              className="block text-xl font-bold text-gray-900 mb-4"
            >
              What's your business idea?
            </label>
            <textarea
              id="business-idea"
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Describe your business idea or problem you want to solve in detail..."
              className="w-full h-40 p-6 border-2 border-gray-300 rounded-2xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 text-gray-900 placeholder-gray-500 resize-none transition-all duration-200 text-lg"
              maxLength={2000}
            />
            
            {/* Character Counter */}
            <div className="flex justify-between items-center mt-3">
              <div className="flex items-center space-x-4">
                <span className={`text-sm font-medium ${getCharacterCount().color}`}>
                  {idea.length}/2000 characters • {getCharacterCount().status}
                </span>
                {selectedExample && (
                  <span className="text-xs bg-sage-100 text-sage-800 px-2 py-1 rounded-full">
                    Example: {selectedExample.metric}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowInspiration(!showInspiration)}
                className="bg-primary-100 hover:bg-primary-200 text-primary-700 hover:text-primary-800 text-sm font-semibold px-3 py-2 rounded-lg border border-primary-300 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                💡 Need inspiration? ▼
              </button>
            </div>
          </div>

          {/* Inspiration Section */}
          {showInspiration && (
            <div className="mb-6 p-6 bg-gray-50 rounded-2xl">
              <h4 className="font-semibold text-gray-900 mb-4">20+ Detailed Examples Across Industries:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {inspirationExamples.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setIdea(example)}
                    className="p-3 text-left text-sm text-gray-700 hover:bg-white hover:text-primary-600 rounded-lg transition-all duration-200"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* CTA Button */}
          <button
            onClick={handleStartDiscovery}
            disabled={!idea.trim() || isLoading}
            className={`w-full py-5 px-8 rounded-2xl font-bold text-xl transition-all duration-200 ${
              !idea.trim() 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-gradient-to-r from-primary-600 to-sage-500 hover:from-primary-700 hover:to-sage-600 text-white shadow-2xl hover:shadow-3xl transform hover:-translate-y-1'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                Analyzing Your Idea...
              </div>
            ) : (
              'Discover My Market Opportunity'
            )}
          </button>

          {/* How it Works */}
          <div id="how-it-works" className="mt-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-16">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 font-bold text-lg">1</div>
                <h4 className="font-semibold text-gray-900 mb-2 h-12 flex items-center justify-center">AI Discovery</h4>
                <p className="text-sm text-gray-600">Smart conversation to refine and validate your concept</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-gradient-to-r from-sage-500 to-sage-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 font-bold text-lg">2</div>
                <h4 className="font-semibold text-gray-900 mb-2 h-12 flex items-center justify-center">Market Segmentation</h4>
                <p className="text-sm text-gray-600">Identify precise target segments using 6 different approaches</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-gradient-to-r from-cream-500 to-cream-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-4 font-bold text-lg">3</div>
                <h4 className="font-semibold text-gray-900 mb-2 h-12 flex items-center justify-center">Strategic Report</h4>
                <p className="text-sm text-gray-600">Export-ready insights and actionable next steps</p>
              </div>
            </div>
          </div>
        </div>


  

        {/* Trust & Credibility Bar */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 font-medium mb-6">Powered by:</p>
          <div className="flex items-center justify-center space-x-8 mb-8 flex-wrap">
            <div className="flex items-center space-x-2 text-gray-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <span className="text-sm font-medium">Industry databases</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="text-sm font-medium">Market research APIs</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="text-sm font-medium">AI analysis</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-500">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-sm font-medium">Real-time data</span>
            </div>
          </div>
          <p className="text-gray-500 text-sm">
 
            <span className="text-primary-600 font-medium"> Results in 3 minutes</span> • 
            <span className="text-sage-600 font-medium"> Export-ready reports</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;